import { useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@/components/ui/modal'
import { useInvoices } from '@/hooks/use-billing'
import { useRecordPayment } from '@/hooks/use-payments'
import { useUtilityTypes } from '@/hooks/use-utilities'
import { getApiErrorMessage } from '@/lib/api'
import { formatKES } from '@/lib/utils'
import type { PaymentMethod, RecordPaymentResult } from '@riverpark/types'

const SELECT_CLS =
  'flex h-10 w-full rounded border border-input bg-card px-3.5 font-sans text-sm text-ink focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-green/20'

const METHODS: {
  value: Extract<PaymentMethod, 'cash' | 'bank_transfer' | 'manual_mpesa'>
  label: string
}[] = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank transfer' },
  { value: 'manual_mpesa', label: 'M-Pesa (recorded manually)' },
]

function todayDateInputValue() {
  return new Date().toISOString().slice(0, 10)
}

type ApplyMode = 'everything' | 'invoice' | 'utilityType'

export function RecordPaymentModal({
  memberId,
  memberName,
  onClose,
  presetInvoiceId,
  presetInvoiceNumber,
}: {
  memberId: string
  memberName: string
  onClose: () => void
  /** Passed when opened from that invoice's own detail view — the payment always targets this
   * invoice, no picker shown. There's nothing to choose: staff already navigated to one specific
   * bill. To record a whole-balance or utility-type payment instead, open Record Payment from the
   * member's profile, not from an invoice. */
  presetInvoiceId?: string
  presetInvoiceNumber?: string
}) {
  const record = useRecordPayment()
  const isPreset = !!presetInvoiceId

  const [amount, setAmount] = useState('')
  const [method, setMethod] = useState<(typeof METHODS)[number]['value']>('cash')
  const [reference, setReference] = useState('')
  const [notes, setNotes] = useState('')
  const [paidAt, setPaidAt] = useState(todayDateInputValue())
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<RecordPaymentResult | null>(null)

  const [applyMode, setApplyMode] = useState<ApplyMode>('everything')
  const [invoiceId, setInvoiceId] = useState('')
  const [utilityTypeId, setUtilityTypeId] = useState('')

  const { data: invoicesData } = useInvoices({
    memberId,
    excludeInvoiceType: 'credit_note',
    limit: 100,
  })
  const openInvoices = (invoicesData?.data ?? []).filter(
    (inv) => inv.status !== 'paid' && inv.status !== 'voided',
  )
  const { data: utilityTypesData } = useUtilityTypes(!isPreset && applyMode === 'utilityType')
  const utilityTypes = utilityTypesData?.data ?? []

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)

    const amountNum = parseFloat(amount)
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      setError('Enter a valid payment amount')
      return
    }
    if (!isPreset && applyMode === 'invoice' && !invoiceId) {
      setError('Choose which invoice to apply this to')
      return
    }
    if (!isPreset && applyMode === 'utilityType' && !utilityTypeId) {
      setError('Choose which utility type to apply this to')
      return
    }

    try {
      const { data } = await record.mutateAsync({
        memberId,
        amount: amountNum,
        method,
        paidAt: new Date(paidAt).toISOString(),
        ...(reference && { reference }),
        ...(notes && { notes }),
        ...(isPreset && { targetInvoiceId: presetInvoiceId }),
        ...(!isPreset && applyMode === 'invoice' && { targetInvoiceId: invoiceId }),
        ...(!isPreset && applyMode === 'utilityType' && { targetUtilityTypeId: utilityTypeId }),
      })
      setResult(data)
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to record payment'))
    }
  }

  return (
    <Modal open onClose={onClose} labelledBy="record-payment-title">
      <ModalHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-sans text-label uppercase tracking-[0.12em] text-muted-foreground">
              {memberName}
            </p>
            <h2 id="record-payment-title" className="font-serif text-xl text-ink">
              Record payment
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-ink"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
      </ModalHeader>

      {result ? (
        <>
          <ModalBody className="space-y-3">
            {result.applications.length > 0 && (
              <>
                <p className="font-sans text-sm text-ink">
                  Applied to {result.applications.length} invoice(s)
                  {!isPreset && applyMode === 'everything' ? ', oldest first' : ''}:
                </p>
                <ul className="space-y-1.5">
                  {result.applications.map((a) => (
                    <li
                      key={a.invoiceId}
                      className="flex items-center justify-between rounded border border-border-2 bg-cream-2 px-3 py-2 font-sans text-sm"
                    >
                      <span className="text-ink">{a.invoiceNumber}</span>
                      <span className="text-muted-foreground">
                        {formatKES(a.amountApplied)} — now {a.invoiceStatus}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {result.creditAmount > 0 && (
              <p className="rounded border border-border-2 bg-cream-2 px-3 py-2 font-sans text-xs text-muted-foreground">
                {result.applications.length > 0
                  ? `${formatKES(result.creditAmount)} exceeded everything this member owed — recorded as a credit against future invoices.`
                  : `Nothing was outstanding to apply this to — the full ${formatKES(result.creditAmount)} was recorded as a credit against future invoices${applyMode === 'utilityType' ? ' for this utility type' : ''}.`}
              </p>
            )}
          </ModalBody>
          <ModalFooter>
            <Button type="button" onClick={onClose}>
              Done
            </Button>
          </ModalFooter>
        </>
      ) : (
        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <ModalBody className="space-y-4">
            {isPreset ? (
              <p className="font-sans text-sm text-muted-foreground">
                Applied to invoice{' '}
                <span className="font-medium text-ink">{presetInvoiceNumber}</span> only.
              </p>
            ) : (
              <>
                <div className="space-y-1.5">
                  <label className="font-sans text-sm font-medium text-ink">Apply to</label>
                  <select
                    className={SELECT_CLS}
                    value={applyMode}
                    onChange={(e) => setApplyMode(e.target.value as ApplyMode)}
                  >
                    <option value="everything">Whole balance (oldest invoice first)</option>
                    <option value="invoice">One invoice</option>
                    <option value="utilityType">One utility type</option>
                  </select>
                  {applyMode === 'everything' && (
                    <p className="font-sans text-xs text-muted-foreground">
                      Applied to this member&apos;s oldest outstanding invoice first, overflow
                      cascading to the next.
                    </p>
                  )}
                </div>
                {applyMode === 'invoice' && (
                  <div className="space-y-1.5">
                    <label className="font-sans text-sm font-medium text-ink">Invoice</label>
                    <select
                      className={SELECT_CLS}
                      value={invoiceId}
                      onChange={(e) => setInvoiceId(e.target.value)}
                    >
                      <option value="">Select an invoice…</option>
                      {openInvoices.map((inv) => (
                        <option key={inv.id} value={inv.id}>
                          {inv.invoiceNumber} — {formatKES(inv.balance)} due
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {applyMode === 'utilityType' && (
                  <div className="space-y-1.5">
                    <label className="font-sans text-sm font-medium text-ink">Utility type</label>
                    <select
                      className={SELECT_CLS}
                      value={utilityTypeId}
                      onChange={(e) => setUtilityTypeId(e.target.value)}
                    >
                      <option value="">Select a utility type…</option>
                      {utilityTypes.map((ut) => (
                        <option key={ut.id} value={ut.id}>
                          {ut.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </>
            )}
            <Input
              label="Amount (KES)"
              type="number"
              step="any"
              min={0}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
            />
            <div className="space-y-1.5">
              <label className="font-sans text-sm font-medium text-ink">Method</label>
              <select
                className={SELECT_CLS}
                value={method}
                onChange={(e) => setMethod(e.target.value as typeof method)}
              >
                {METHODS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
            <Input
              label="Reference (optional)"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Receipt / transaction number"
            />
            <Input
              label="Date received"
              type="date"
              value={paidAt}
              onChange={(e) => setPaidAt(e.target.value)}
              required
            />
            <div className="space-y-1.5">
              <label htmlFor="payment-notes" className="font-sans text-sm font-medium text-ink">
                Notes (optional)
              </label>
              <textarea
                id="payment-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="flex w-full rounded border border-input bg-card px-3.5 py-2 font-sans text-sm text-ink focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-green/20"
              />
            </div>
            {error && (
              <p className="font-sans text-xs text-destructive" role="alert">
                {error}
              </p>
            )}
          </ModalBody>
          <ModalFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={record.isPending}>
              Record payment
            </Button>
          </ModalFooter>
        </form>
      )}
    </Modal>
  )
}
